/**
 * Date Navigator Component
 * Week/day navigation for RSVP reports with mode toggle
 */

import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  formatWeekRange,
  formatDate,
  prevWeek,
  nextWeek,
  getCurrentWeekStart,
  isSameDate,
  toISODate,
  fromISODate,
} from '@/lib/utils/dateHelpers';
import { addDays, subDays } from 'date-fns';
import type { ReportDateMode } from '@/types/reportMode';

export type DateMode = ReportDateMode;

interface DateNavigatorProps {
  /** Current date (start of week for week mode, single date for day mode) */
  currentDate: Date;
  /** Callback when date changes */
  onDateChange: (newDate: Date) => void;
  /** Navigation mode */
  mode: DateMode;
  /** Callback when mode changes */
  onModeChange: (mode: DateMode) => void;
  /** Disable navigation controls */
  disabled?: boolean;
}

/**
 * DateNavigator - Navigate between weeks or single days
 *
 * In week mode: Shows "Jan 13 - Jan 19, 2025" format with prev/next week buttons
 * In day-like modes: Shows "Monday, January 13, 2025" format with prev/next day buttons and date picker
 */
export function DateNavigator({
  currentDate,
  onDateChange,
  mode,
  onModeChange,
  disabled = false,
}: DateNavigatorProps) {
  const isWeekMode = mode === 'week';
  const isDayMode = mode === 'day';
  const isCookingDayMode = mode === 'cookingDay';
  const isDayLikeMode = !isWeekMode;

  const handlePrev = () => {
    if (isWeekMode) {
      onDateChange(prevWeek(currentDate));
    } else {
      onDateChange(subDays(currentDate, 1));
    }
  };

  const handleNext = () => {
    if (isWeekMode) {
      onDateChange(nextWeek(currentDate));
    } else {
      onDateChange(addDays(currentDate, 1));
    }
  };

  const handleToday = () => {
    onDateChange(isWeekMode ? getCurrentWeekStart() : new Date());
  };

  const handleDatePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = fromISODate(e.target.value);
    onDateChange(selectedDate);
  };

  // Check if viewing current week
  const currentWeekStart = getCurrentWeekStart();
  const isCurrentPeriod = isWeekMode
    ? isSameDate(currentDate, currentWeekStart)
    : isSameDate(currentDate, new Date());

  // Format label based on mode
  const label = isWeekMode
    ? formatWeekRange(currentDate)
    : formatDate(currentDate, 'EEEE, MMMM d, yyyy');

  return (
    <div className="space-y-4">
      {/* Mode Toggle */}
      <div className="flex items-center justify-center gap-2">
        <Button
          variant={isWeekMode ? 'default' : 'outline'}
          size="sm"
          onClick={() => onModeChange('week')}
          disabled={disabled}
        >
          Full Week
        </Button>
        <Button
          variant={isDayMode ? 'default' : 'outline'}
          size="sm"
          onClick={() => onModeChange('day')}
          disabled={disabled}
        >
          Single Day
        </Button>
        <Button
          variant={isCookingDayMode ? 'default' : 'outline'}
          size="sm"
          onClick={() => onModeChange('cookingDay')}
          disabled={disabled}
        >
          Cooking Day
        </Button>
      </div>

      {/* Date Navigation */}
      <div className="flex items-center justify-between gap-4">
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={handlePrev}
          disabled={disabled}
          aria-label={isWeekMode ? 'Previous week' : 'Previous day'}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <div className="flex items-center gap-3 flex-1 justify-center">
          <CalendarDays className="h-5 w-5 text-muted-foreground flex-shrink-0" />
          <span className="text-lg font-medium text-center">{label}</span>
          {!isCurrentPeriod && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleToday}
              disabled={disabled}
              className="text-muted-foreground hover:text-foreground"
            >
              Today
            </Button>
          )}
        </div>

        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={handleNext}
          disabled={disabled}
          aria-label={isWeekMode ? 'Next week' : 'Next day'}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Date Picker (day mode only) */}
      {isDayLikeMode && (
        <div className="flex justify-center">
          <input
            type="date"
            value={toISODate(currentDate)}
            onChange={handleDatePickerChange}
            disabled={disabled}
            className="border border-input bg-background rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="Select date"
          />
        </div>
      )}
    </div>
  );
}
